/**
 * @fileoverview Generating Phase Component
 * @module GeneratingPhase
 * 
 * Business Context:
 * - Third phase of the Build Your Case wizard
 * - Clean, focused loading experience
 * - Subtle progress indication without overwhelming
 */

import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  FileText,
  MessageSquare,
  Check,
  Lightbulb
} from 'lucide-react';

interface GeneratingPhaseProps {
  isGenerating: boolean;
}

interface GenerationStep {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const GENERATION_STEPS: GenerationStep[] = [
  { id: 'analyzing', label: 'Analyzing evidence', icon: <FileText className="w-4 h-4" /> },
  { id: 'building', label: 'Building arguments', icon: <Sparkles className="w-4 h-4" /> },
  { id: 'formatting', label: 'Formatting script', icon: <MessageSquare className="w-4 h-4" /> }
];

const TIPS = [
  "Arrive 10-15 minutes early to your meeting",
  "Dress comfortably - it's an informal conversation",
  "Bring printed copies of your evidence",
  "Stay calm and friendly - the appraiser is there to help",
  "Listen to what the appraiser says about your property"
];

const GeneratingPhase: React.FC<GeneratingPhaseProps> = ({ isGenerating }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [tipIndex, setTipIndex] = useState(0);

  // Progress through steps
  useEffect(() => {
    if (!isGenerating) return;

    const interval = setInterval(() => {
      setCurrentStepIndex((prev) => {
        if (prev < GENERATION_STEPS.length - 1) {
          setCompletedSteps((completed) => new Set([...completed, prev]));
          return prev + 1;
        }
        return prev;
      });
    }, 2500);

    return () => clearInterval(interval);
  }, [isGenerating]);

  // Rotate tips
  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % TIPS.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-teal-600 px-6 py-10 text-center text-white">
        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-7 h-7 animate-pulse" />
        </div>
        <h2 className="text-xl font-semibold mb-1">Creating Your Script</h2>
        <p className="text-teal-100 text-sm">
          Crafting personalized talking points from your evidence
        </p>
      </div>

      {/* Progress Steps */}
      <div className="px-6 py-6">
        <div className="space-y-3 mb-6">
          {GENERATION_STEPS.map((step, index) => {
            const isCompleted = completedSteps.has(index);
            const isCurrent = index === currentStepIndex && !isCompleted;
            const isPending = index > currentStepIndex;

            return (
              <div
                key={step.id}
                className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                  isCompleted ? 'bg-teal-50' : isCurrent ? 'bg-slate-50' : 'bg-slate-50/50'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    isCompleted
                      ? 'bg-teal-600 text-white'
                      : isCurrent
                      ? 'bg-teal-100 text-teal-600'
                      : 'bg-slate-200 text-slate-400'
                  }`}
                >
                  {isCompleted ? <Check className="w-4 h-4" /> : step.icon}
                </div>
                <span
                  className={`text-sm font-medium ${
                    isCompleted ? 'text-teal-700' : isCurrent ? 'text-slate-700' : 'text-slate-400'
                  }`}
                >
                  {step.label}
                </span>
                {isCurrent && (
                  <div className="ml-auto w-4 h-4 border-2 border-teal-300 border-t-teal-600 rounded-full animate-spin" />
                )}
              </div>
            );
          })}
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-slate-400 mb-1.5">
            <span>Progress</span>
            <span>~1 minute</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal-500 rounded-full transition-all duration-700"
              style={{
                width: `${((currentStepIndex + (isGenerating ? 0.5 : 0)) / GENERATION_STEPS.length) * 100}%`
              }}
            />
          </div>
        </div>

        {/* Tip */}
        <div className="bg-amber-50 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Lightbulb className="w-4 h-4 text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-amber-700 uppercase tracking-wide mb-0.5">
                Meeting Tip
              </p>
              <p className="text-sm text-amber-800">{TIPS[tipIndex]}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneratingPhase;
